import { JSX, ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { asTitle } from "utils/Utils";

import { Report } from "core/blueprint/trajectory/TrajectoryBlueprintService";
import {
	Field,
	FieldType,
	GroupField,
	CompetenceListField
} from "core/field/FieldService";

export interface ReportSubHeaderProps {
	reports: Array<Report>;
	selection: Array<string>;
	selectedFields: string[];

	onSelect: (ids: Array<string>) => void;
	onSelectFields: (ids: string[]) => void;
}

type SelectionOption = {
	uuid: string;
	index: string;
	name: string;
};

function ReportSubHeader(props: Readonly<ReportSubHeaderProps>): JSX.Element {
	const { t } = useTranslation();

	const selectionOptions: SelectionOption[] =
		useMemo((): SelectionOption[] => {
			if (props.selection.length !== 1 || props.selection.includes("")) {
				return [];
			}

			const report =
				props.reports.find(r => r.name === props.selection[0]) ?? null;

			if (report === null) {
				return [];
			}

			const flattenedList: SelectionOption[] = [];
			flattenedList.push(...flatFieldList(report.fields));
			return flattenedList;
		}, [props.reports, props.selection]);

	function flatFieldList(fields: Field[], prefix = ""): SelectionOption[] {
		return fields
			.map((field, index) => {
				const entryIndex = prefix + (index + 1);
				const entries = [
					{
						uuid: field.uuid,
						index: entryIndex,
						name: field.name
					}
				];

				if (field.type === FieldType.GROUP) {
					const group = field as GroupField;
					entries.push(
						...flatFieldList(group.children, entryIndex + ".")
					);
				} else if (field.type === FieldType.COMPETENCE_LIST) {
					const competenceList = field as CompetenceListField;
					const levelFields = competenceList.levels
						.map(l => l.elements)
						.flat();
					entries.push(
						...flatFieldList(levelFields, entryIndex + ".")
					);
				}

				return entries;
			})
			.flat();
	}

	const processSelect = useCallback(
		(value: string | string[], f: (v: Array<string>) => void): void => {
			if (typeof value === "string") {
				f(value.split(","));
			} else {
				f(value);
			}
		},
		[]
	);

	const handleSelect = useCallback(
		(event: SelectChangeEvent<Array<string>>): void => {
			processSelect(event.target.value, props.onSelect);
			processSelect([], props.onSelectFields);
		},
		[props.onSelect, props.onSelectFields]
	);

	const handleSelectFields = useCallback(
		(event: SelectChangeEvent<Array<string>>): void => {
			processSelect(event.target.value, props.onSelectFields);
		},
		[props.onSelectFields]
	);

	const renderValue = useCallback((selected: Array<string>): ReactNode => {
		if (selected.length === 0) {
			return <em>{asTitle(t("selectOneOrMoreParts"))}</em>;
		}

		return selected
			.map(value => (value === "" ? `* ${t("everything")}` : value))
			.join(", ");
	}, []);

	const renderFieldValue = useCallback(
		(selected: string[]): ReactNode => {
			if (selected.length === 0) {
				return <em>{asTitle(t("selectOneOrMoreParts"))}</em>;
			}

			return selectionOptions
				.filter(field => selected.includes(field.uuid))
				.map(field => `${field.index} ${field.name}`)
				.join(", ");
		},
		[selectionOptions]
	);

	return (
		<Grid container alignItems="center">
			<Grid item xs={6} sm={7} md={6} lg={7} />
			<Grid item xs={6} sm={5} md={4} lg={3}>
				<Box sx={{ position: "relative", height: "32px" }}>
					<Select
						variant="standard"
						sx={{ position: "absolute", width: "100%" }}
						multiple
						displayEmpty
						value={props.selection}
						onChange={handleSelect}
						renderValue={renderValue}>
						<MenuItem value={""}>{`* ${t("everything")}`}</MenuItem>
						{props.reports.map(report => (
							<MenuItem key={report.id} value={report.name}>
								{report.name}
							</MenuItem>
						))}
					</Select>
				</Box>
			</Grid>

			{selectionOptions.length > 0 && (
				<>
					<Grid item xs={6} sm={7} md={6} lg={7} />
					<Grid item xs={6} sm={5} md={4} lg={3}>
						<Box sx={{ position: "relative", height: "32px" }}>
							<Select
								variant="standard"
								sx={{ position: "absolute", width: "100%" }}
								multiple
								displayEmpty
								value={props.selectedFields}
								onChange={handleSelectFields}
								renderValue={renderFieldValue}>
								{selectionOptions.map(field => (
									<MenuItem
										key={field.uuid}
										value={field.uuid}>
										{field.index} {field.name}
									</MenuItem>
								))}
							</Select>
						</Box>
					</Grid>
				</>
			)}
		</Grid>
	);
}

export default ReportSubHeader;
